/**
 * Device ID utilities.
 *
 * - In Electron (desktop) we fetch the real MAC via IPC on first launch and cache it.
 * - In Capacitor Android (v1.3.76) we fetch the real MAC via the
 *   `NativeDevice` plugin (eth0 / wlan0 / arp / synth fallback chain).
 * - In a browser preview we fall back to a stable pseudo-MAC stored in localStorage.
 *
 * `initDeviceId()` should be awaited once at app boot so the real MAC is used
 * before the activation screen renders. After that, `getDeviceId()` is sync.
 *
 * v1.4.16 — MAC persistence hardened. Every write goes to BOTH
 * `localStorage.vupp_device_id` AND an IndexedDB record
 * (`za-player-pro` / `device` / `mac`). On boot we hydrate from
 * whichever survives, so clearing the localStorage cache (browser
 * "Clear site data" for storage only, or an app reinstall that wipes
 * localStorage but leaves IndexedDB) no longer regenerates a fresh
 * pseudo-MAC — the original MAC is recovered. This makes the MAC
 * stable across the widest range of user actions on every platform.
 *
 * The MAC source is tracked separately in `vupp_device_mac_source`
 * (e.g. "sys:eth0", "ni:wlan0", "synth:android_id", "electron",
 * "pseudo") so the Activation screen can show the user where the
 * MAC came from — and so support staff can tell at a glance
 * whether a "MAC" they see is a real hardware address or a
 * synthesised one bound to ANDROID_ID.
 */
const DEVICE_ID_KEY = "vupp_device_id";
const DEVICE_MAC_SOURCE_KEY = "vupp_device_mac_source";
const IDB_NAME = "za-player-pro";
const IDB_STORE = "device";
const IDB_KEY_MAC = "mac";
const IDB_KEY_MAC_SOURCE = "mac_source";

function generatePseudoMac() {
  const hex = (n) =>
    Array.from({ length: n }, () =>
      Math.floor(Math.random() * 256)
        .toString(16)
        .padStart(2, "0")
        .toUpperCase()
    );
  return ["02", ...hex(5)].join(":");
}

// --- IndexedDB helpers (durable backup for the MAC) --------------------
function _openIdb() {
  if (typeof indexedDB === "undefined") return Promise.resolve(null);
  return new Promise((resolve) => {
    try {
      const req = indexedDB.open(IDB_NAME, 1);
      req.onupgradeneeded = () => {
        try {
          req.result.createObjectStore(IDB_STORE);
        } catch { /* store already exists */ }
      };
      req.onsuccess = () => resolve(req.result);
      req.onerror = () => resolve(null);
      req.onblocked = () => resolve(null);
    } catch {
      resolve(null);
    }
  });
}

async function _idbGet(key) {
  const db = await _openIdb();
  if (!db) return null;
  return new Promise((resolve) => {
    try {
      const tx = db.transaction(IDB_STORE, "readonly");
      const req = tx.objectStore(IDB_STORE).get(key);
      req.onsuccess = () => resolve(req.result || null);
      req.onerror = () => resolve(null);
    } catch {
      resolve(null);
    }
  });
}

async function _idbPut(key, value) {
  const db = await _openIdb();
  if (!db) return;
  await new Promise((resolve) => {
    try {
      const tx = db.transaction(IDB_STORE, "readwrite");
      tx.objectStore(IDB_STORE).put(value, key);
      tx.oncomplete = () => resolve();
      tx.onerror = () => resolve();
      tx.onabort = () => resolve();
    } catch {
      resolve();
    }
  });
}

async function _idbDelete(key) {
  const db = await _openIdb();
  if (!db) return;
  await new Promise((resolve) => {
    try {
      const tx = db.transaction(IDB_STORE, "readwrite");
      tx.objectStore(IDB_STORE).delete(key);
      tx.oncomplete = () => resolve();
      tx.onerror = () => resolve();
    } catch {
      resolve();
    }
  });
}

// Write the MAC to every durable store we have (localStorage + IndexedDB).
async function _persistMac(id, source) {
  try { localStorage.setItem(DEVICE_ID_KEY, id); } catch { /* private mode */ }
  if (source) {
    try { localStorage.setItem(DEVICE_MAC_SOURCE_KEY, source); } catch { /* private mode */ }
  }
  // Fire-and-forget — do not block boot on IDB write.
  _idbPut(IDB_KEY_MAC, id).catch(() => {});
  if (source) _idbPut(IDB_KEY_MAC_SOURCE, source).catch(() => {});
}

export async function initDeviceId() {
  // Authoritative source of truth = IndexedDB. It survives the widest
  // range of "clear data" actions and app-install cycles. Only fall back
  // to localStorage / OS / freshly-generated MAC if IDB has nothing.
  const persisted = await _idbGet(IDB_KEY_MAC);
  if (persisted && typeof persisted === "string") {
    const persistedSource = (await _idbGet(IDB_KEY_MAC_SOURCE)) || undefined;
    // Even if localStorage was just repopulated with a *different* value
    // by a sync `getDeviceId()` call earlier in the boot tick, IDB wins.
    try { localStorage.setItem(DEVICE_ID_KEY, persisted); } catch { /* ignore */ }
    if (persistedSource) {
      try { localStorage.setItem(DEVICE_MAC_SOURCE_KEY, persistedSource); } catch { /* ignore */ }
    }
    return persisted;
  }

  // No IDB record yet — fall back to localStorage cache (may have been
  // written by a previous release that predates IDB persistence).
  let id = localStorage.getItem(DEVICE_ID_KEY);
  if (id) {
    _idbPut(IDB_KEY_MAC, id).catch(() => {});
    return id;
  }

  let source = null;

  // Electron path — pull the real MAC via IPC.
  if (typeof window !== "undefined" && window.electronAPI?.getMacAddress) {
    try {
      id = await window.electronAPI.getMacAddress();
      if (id) source = "electron";
    } catch {
      id = null;
    }
  }

  // v1.3.76 — Capacitor Android: ask the NativeDevice plugin for the
  // real hardware MAC. The plugin walks eth0 → wlan0 → arp → synth,
  // so by the time it resolves we have *some* deterministic MAC.
  if (
    !id
    && typeof window !== "undefined"
    && window.Capacitor?.isNativePlatform?.()
    && window.Capacitor?.getPlatform?.() === "android"
  ) {
    try {
      // Dynamic import so the browser/Electron bundles don't try
      // to load `@capacitor/core` at module-eval time.
      const core = await import("@capacitor/core");
      const NativeDevice = core.registerPlugin("NativeDevice");
      const res = await NativeDevice.getMacAddress();
      if (res && res.mac) {
        id = res.mac;
        source = res.source || "native";
      }
    } catch {
      // Plugin missing (older Android build) or call failed —
      // fall through to pseudo-MAC. The user can still activate;
      // they'll just see a generated MAC.
    }
  }

  if (!id) {
    id = generatePseudoMac();
    source = "pseudo";
  }
  await _persistMac(id, source);
  return id;
}

export function getDeviceId() {
  let id = localStorage.getItem(DEVICE_ID_KEY);
  if (!id) {
    // Sync fallback. We deliberately DO NOT touch IndexedDB here —
    // `initDeviceId()` is the only writer to IDB, so a cold-start
    // sync placeholder can be overwritten later once the async
    // recovery finishes.
    id = generatePseudoMac();
    try { localStorage.setItem(DEVICE_ID_KEY, id); } catch { /* private mode */ }
  }
  return id;
}

export function resetDeviceId() {
  try { localStorage.removeItem(DEVICE_ID_KEY); } catch { /* ignore */ }
  try { localStorage.removeItem(DEVICE_MAC_SOURCE_KEY); } catch { /* ignore */ }
  _idbDelete(IDB_KEY_MAC).catch(() => {});
  _idbDelete(IDB_KEY_MAC_SOURCE).catch(() => {});
}

/**
 * v1.3.76 — Returns the source tag set by `initDeviceId()`:
 *   "electron"           — read from Electron IPC (desktop)
 *   "sys:eth0"           — /sys/class/net/eth0/address (Android TV cabled)
 *   "sys:wlan0"          — /sys/class/net/wlan0/address (Android TV WiFi)
 *   "ni:eth0" | "ni:wlan0" — Java NetworkInterface API fallback
 *   "arp"                — last-resort /proc/net/arp scan
 *   "synth:android_id"   — synthesised MAC bound to ANDROID_ID
 *                          (only when no hardware MAC was readable)
 *   "pseudo"             — random pseudo-MAC (browser preview)
 *   null                 — not initialised yet
 *
 * The Activation screen uses this so the user (and the provider
 * support team) can tell whether the MAC shown is a real hardware
 * address or a synthesised one.
 */
export function getDeviceMacSource() {
  if (typeof localStorage === "undefined") return null;
  return localStorage.getItem(DEVICE_MAC_SOURCE_KEY);
}

export function detectPlatform() {
  if (typeof window !== "undefined" && window.electronAPI?.platform) {
    return window.electronAPI.platform;
  }
  const ua = navigator.userAgent;
  if (/Android/i.test(ua)) return "android";
  if (/iPhone|iPad|iPod/i.test(ua)) return "ios";
  if (/Mac/i.test(ua)) return "macos";
  if (/Linux/i.test(ua)) return "linux";
  if (/Win/i.test(ua)) return "windows";
  return "web";
}

export function isElectron() {
  return !!(typeof window !== "undefined" && window.electronAPI?.isElectron);
}

export function isAndroid() {
  if (typeof window === "undefined") return false;
  // Electron always wins — the desktop wrapper uses HTML5 + bundled mpv,
  // never the Android native plugins.
  if (window.electronAPI?.isElectron) return false;
  // Capacitor (native APK)
  if (window.Capacitor?.isNativePlatform?.() && window.Capacitor?.getPlatform?.() === "android")
    return true;
  // Compile-time hint (set by `yarn build:web` in /app/android-app)
  if (process.env.REACT_APP_PLATFORM === "android") return true;
  return false;
}

export function isCapacitor() {
  return !!(typeof window !== "undefined" && window.Capacitor?.isNativePlatform?.());
}
